import { z } from 'zod';

export type PresentationsBrandsCategory = {
  label: string;
  description: string;
  comingSoon?: boolean;
};

export enum PresentationsTabsPath {
  type = '/account/presentations/type',
  selections = '/account/presentations/selections',
  brands = '/account/presentations/brands',
  confirmations = '/account/presentations/confirmation'
}

export enum PresentationsTab {
  Type = 'type',
  Selections = 'selections',
  Brands = 'brands',
  Confirmation = 'confirmation'
}

export const PresentationsDataEntityTypeSchema = z.enum(['category', 'segment', 'businessunit']);
export type PresentationsDataEntityType = z.infer<typeof PresentationsDataEntityTypeSchema>;

export interface SelectOption {
  id: string | number;
  value: string;
  displayName: string;
}

export type ReportRequest = {
  retailerIds: number[];
  entityType?: PresentationsDataEntityType;
  entityId?: string; // Only for non-category entities
  currentStartWeekId: number;
  currentEndWeekId: number;
  comparisonStartWeekId: number;
  comparisonEndWeekId: number;
  categoryIds?: number[];
  subcategoryIds?: string[];
  destinationCurrency: string;
  reportType: string;
  modelName: string;
};

/**
 * Options for generating a presentation report from the API.
 * Used when the report type is fetched from the API (isFromAPI: true).
 */
export interface GenerateReportFromAPIOptions {
  categoryIds?: number[];
  subcategoryIds?: number[];
  brandIds?: number[];
  retailerIds?: number[];
  startWeekId: number;
  endWeekId: number;
  comparisonPeriod: string;
  comparisonStartWeekId: number;
  comparisonEndWeekId: number;
  timePeriod: string;
  segmentId?: string;
  businessUnitId?: string;
  reportCurrency: string;
}

/**
 * Request body for generating a report from the API.
 * Used when the presentation type is fetched from the API.
 */
export interface GenerateReportFromAPIRequest {
  presentationId: string;
  options: GenerateReportFromAPIOptions;
}

/**
 * Response from POST /api/stories/v2/report when creating a new presentation report.
 */
export interface GenerateReportFromAPIResponse {
  requestId: string;
  status: PresentationDownloadStatus;
  message?: string;
}

/**
 * Possible status values for a presentation download.
 * Note: API may return uppercase (COMPLETE) or lowercase (completed) values.
 */
export type PresentationDownloadStatus =
  | 'COMPLETE'
  | 'FAILED'
  | 'IN_QUEUE'
  | 'PROCESSING'
  | 'RETRYING'
  | 'DONE'
  | 'REQUESTED'
  | 'IN_PROGRESS';

/**
 * Individual presentation download item from the GET /api/stories/v2/reports response.
 */
export interface PresentationDownloadItem {
  /** Division UUID */
  divisionId: string;
  /** User UUID */
  userId: string;
  /** Unique identifier for the report request */
  requestId: string;
  /** Current status of the report generation */
  status: PresentationDownloadStatus;
  /** ISO timestamp when the report was created */
  createdAt: string;
  /** ISO timestamp when the report was last updated */
  updatedAt: string;
  /** S3 bucket name */
  bucket: string;
  /** S3 key/path for the generated file */
  key: string;
  /** Number of times the report has been downloaded */
  numberOfDownloads: number;
  /** Pre-signed S3 download URL (60 min expiry) */
  downloadLink: string;
  /** Presentation ID (V2 only, null for V1) */
  presentationId: string | null;
  /** Request timestamp (V2 only, null for V1) */
  requestTime: string | null;
  /** Source identifier */
  source: 'v1' | 'v2';
}

/**
 * Response from GET /api/stories/v2/reports for listing presentation downloads.
 * Query params: page_size (default: 10), last_key (optional pagination token)
 */
export interface PresentationsDownloadListResponse {
  result: PresentationDownloadItem[];
  paginationToken: string | null;
}

export const FormValueSchema = z.object({
  id: z.string(),
  name: z.string()
});

/**
 * Schema for report type form value.
 * Includes `isFromAPI` flag to distinguish between hardcoded and API-fetched presentation types.
 */
const ReportTypeFormValueSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** True if the presentation type was fetched from the API, false/undefined for hardcoded types */
  isFromAPI: z.boolean().optional()
});

// TypeScript interface for the Presentation object based on new API response
export interface Presentation {
  presentationId: string;
  description: string;
  presentationName: string;
  totalSlides: number;
  tags?: string[];
  updatedTime?: string; // ISO timestamp for sorting API presentations
  /** Optional form configuration from backend - controls field visibility and validation */
  formConfig?: PresentationFormConfig;
}

export interface PresentationsResponse {
  presentations: Presentation[];
  total: number;
  page?: number;
  pageSize?: number;
}

// Step 1: Report Type Schema
export const ReportTypeStepSchema = z.object({
  reportType: ReportTypeFormValueSchema.refine((data) => data && data.id && data.name, { message: 'Report Type is required' })
});

export const SelectionsStepSchema = z.object({
  currency: z.string().nonempty('Currency is required'),
  retailerIds: z.array(FormValueSchema),
  entityType: FormValueSchema,
  entityId: FormValueSchema.optional(),
  categoryIds: z.array(FormValueSchema).optional(),
  subcategoryIds: z.array(FormValueSchema).optional()
})
  .refine(
    (data) => data.retailerIds.length > 0,
    {
      message: 'Please select at least one retailer',
      path: ['retailerIds']
    }
  )
  .superRefine((data, ctx) => {
    if (data.entityType.id === PresentationsDataEntityTypeSchema.Enum.category) {
      const hasCategories = data.categoryIds && data.categoryIds.length > 0;
      const hasSubcategories = data.subcategoryIds && data.subcategoryIds.length > 0;

      if (!hasCategories && !hasSubcategories) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either Categories or Subcategories must be selected',
        });
      }
    }
  })
  .refine(
    (data) => data.entityType.id === PresentationsDataEntityTypeSchema.Enum.category || data.entityId.id,
    (data) => {
      const isSegment = data.entityType.id === PresentationsDataEntityTypeSchema.Enum.segment;
      const prefix = isSegment ? 'Segment' : 'Business Unit';

      return {
        message: prefix + ' cannot be empty',
        path: ['entityId']
      };
    }
  );

// Step 3: Brands Schema
export const BrandsStepSchema = z.object({
  brandGroupings: FormValueSchema
});

export const PresentationFormSchema = z.intersection(ReportTypeStepSchema, SelectionsStepSchema, BrandsStepSchema);

export type ReportTypeStep = z.infer<typeof ReportTypeStepSchema>;
export type SelectionsStep = z.infer<typeof SelectionsStepSchema>;
export type BrandsStep = z.infer<typeof BrandsStepSchema>;

export type PresentationForm = z.infer<typeof PresentationFormSchema>;

export type FormValue = z.infer<typeof FormValueSchema>;

/**
 * Form value type for report type selection.
 * Includes `isFromAPI` flag to distinguish between hardcoded and API-fetched presentation types.
 */
export type ReportTypeFormValue = z.infer<typeof ReportTypeFormValueSchema>;

// =============================================================================
// FORM CONFIGURATION TYPES (Backend-Driven)
// =============================================================================

/**
 * Zod schema for runtime validation of field configuration from API.
 * Each field in the form can be configured with these options.
 * 
 * Note: 
 * - Use minCount >= 1 to make a field required (no separate 'required' field)
 * - maxCount === 1: single select UI (replaces instead of adds)
 * - maxCount > 1 or undefined: multi-select UI
 */
export const FieldConfigSchema = z.object({
  /** Whether the field is visible or hidden in the form */
  visibility: z.enum(['visible', 'hidden']).default('visible'),
  /** Minimum number of items required. If >= 1, field is effectively required. Null treated as undefined. */
  minCount: z.number().int().min(0).nullable().optional().transform(val => val ?? undefined),
  /** Maximum number of items allowed (for array fields). If 1, shows single-select UI. Null treated as undefined. */
  maxCount: z.number().int().min(1).nullable().optional().transform(val => val ?? undefined),
});

export type FieldConfig = z.infer<typeof FieldConfigSchema>;

/**
 * Form field names that can be configured per presentation type.
 * Note: entityType is not configurable - it's auto-derived from entityId visibility.
 */
export const CONFIGURABLE_FORM_FIELDS = [
  'retailerIds',
  'entityId',
  'categoryIds',
  'subcategoryIds',
  'brandIds',
  'brandGroupings',
] as const;

export type ConfigurableFormField = typeof CONFIGURABLE_FORM_FIELDS[number];

/**
 * Zod schema for the full form configuration from API.
 * All fields are optional - missing fields will use defaults.
 * Note: entityType is not configurable - it's auto-derived from entityId visibility.
 */
export const PresentationFormConfigSchema = z.object({
  retailerIds: FieldConfigSchema.optional(),
  entityId: FieldConfigSchema.optional(),
  categoryIds: FieldConfigSchema.optional(),
  subcategoryIds: FieldConfigSchema.optional(),
  brandIds: FieldConfigSchema.optional(),
  brandGroupings: FieldConfigSchema.optional(),
});

export type PresentationFormConfig = z.infer<typeof PresentationFormConfigSchema>;

/**
 * Resolved form config with all fields guaranteed to have values.
 * This is the type after merging API config with defaults.
 * 
 * Note: Use `isMultiSelect(config)` helper to determine if field allows multiple selections.
 */
export type ResolvedFieldConfig = {
  visibility: 'visible' | 'hidden';
  /** If >= 1, field is required */
  minCount: number | undefined;
  maxCount: number | undefined;
};

/**
 * All form fields including non-configurable ones (for internal use).
 */
export type AllFormField = ConfigurableFormField | 'entityType';

export type ResolvedFormConfig = {
  [K in ConfigurableFormField]: ResolvedFieldConfig;
} & {
  /** entityType is auto-derived from entityId visibility - not configurable from API */
  entityType: ResolvedFieldConfig;
};
